<template>
  <div class="container">
    <div class="row" v-if="isLoaded">
      <div class="col-md-12 my-5">
        <h1 v-if="isOrderNew">Nová objednávka</h1>
        <h1 v-else>Úprava objednávky {{ calculationVersion.order_number }}</h1>
        <div class="text-muted">Číslo kalkulace: {{ calculation.number }} <br>Název kalkulace: {{ calculation.name }}
          <br>Verze nabíky: {{ calculationVersion.number }} <br>
          Číslo nabídky: <a :href="urlPdfOffer" target="_blank"> {{ versionName }} </a>
        </div>
        <OrdersBasicInfoSection
            v-if="calculation"
            :details="{
                  name: calculation.name,
                  minProformaValue: minProformaValue,
                  proformaValue: calculation.proformaValue,
                  deliveryTime: calculation.deliveryTime,
                  vat: calculation.vat,
                  delegatedVat: calculation.delegatedVat,
                  internalNote: calculation.internalNote
                }"
            v-model:proformaValueAbsolute="proformaValueAbsolute"
            v-model:proformaValidation="proformaValidation"
            v-model:isSectionValid="sectionValidates.basic"
            :proformaValuePercent="calculatedProformaValue"
            :maxProformaValue="czechNumberToNumber(totalPriceWithVatRounded)"
            @update:details="Object.assign(calculation, $event)"
            :isCollapsible="true"
            :defaultOpen="true"
        />

        <OrdersCustomerSection
            v-if="calculation"
            v-model:customer="calculation.customer"
            v-model:deliveryAddress="calculation.deliveryAddress"
            v-model:isSectionValid="sectionValidates.customer"
            :isCollapsible="true"
            :defaultOpen="true"
        />

        <OrdersProformaSection
            v-if="calculation && calculationVersion"
            :orderNumber="calculationVersion.order_number"
            :calculationName="calculation.name"
            :commissionRate="czechNumberToNumber(calculationVersion.data.calculation_summary.commission_rate || 0)"
            :commissionPrice="czechNumberToNumber(calculationVersion.data.calculation_summary.commission_price || 0)"
            v-model:due="proformaDue"
            v-model:payment_method="proformaPaymentMethod"
            v-model:line_text="proformaLineText"
            v-model:proformaCreate="proformaCreate"
            v-model:commissionRate="commissionRate"
            v-model:commissionPrice="commissionPrice"
            @update:isSectionValid="sectionValidates.proforma = $event"
            :isCollapsible="true"
            :defaultOpen="true"
        />

        <OrdersDetailSection
            v-if="calculation && calculationVersion"
            v-model:orderText="calculationVersion.order_text"
            v-model:isSectionValid="sectionValidates.detail"
            :proformaValueAbsolute="proformaValueAbsolute"
            :totalPriceWithoutVat="totalPriceWithoutVat"
            :vat="calculation.vat"
            :totalVat="totalVat"
            :vatCorrection="vatCorrection"
            :totalPriceWithVatRounded="totalPriceWithVatRounded"
        />
      </div>

      <!-- Akční tlačítka -->

      <div class="row my-3">
        <div class="col-md-12 text-end">
          <button type="button"
                  class="btn btn-danger me-2"
                  @click="cancelOrder">
            Zrušit
          </button>
          <button v-if="isOrderNew"
                  type="button"
                  class="btn btn-success"
                  @click="createOrder"
                  :disabled="!isFormValid">
            Vytvořit objednávku
          </button>
          <button v-else
                  type="button"
                  class="btn btn-success"
                  @click="createOrder"
                  :disabled="!isFormValid">
            Upravit objednávku
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {defineProps, ref, computed, onMounted} from 'vue';
import {useRouter} from 'vue-router';
import {useStore} from 'vuex';
import myApi from "@/api/api";
import {czechNumberToNumber, formatFloat} from "@/common/functions";
import OrdersBasicInfoSection from "@/components/orders/OrdersBasicInfoSection.vue";
import OrdersCustomerSection from "@/components/orders/OrdersCustomerSection.vue";
import OrdersDetailSection from "@/components/orders/OrdersDetailSection.vue";
import OrdersProformaSection from "@/components/orders/OrdersProformaSection.vue";

const props = defineProps({version: String})
const router = useRouter()
const store = useStore()

// Reactive data
const calculationVersion = ref(null)
const calculation = ref(null)
const isLoaded = ref(false)
const proformaValueAbsolute = ref(null)
const proformaValidation = ref(null)
const proformaDue = ref(14)
const proformaPaymentMethod = ref('bank')
const proformaLineText = ref()
const proformaCreate = ref(true)
const commissionRate = ref(0)
const commissionPrice = ref(0)

const sectionValidates = ref({
  basic: false,
  customer: false,
  proforma: false,
  detail: false
})

const isOrderNew = computed(() => {
  return !calculationVersion.value.order_number
})

const totalPriceWithoutVat = computed(() => {
  const tp = calculationVersion.value.data.calculation_summary.total_price_without_dph
  if (typeof tp === 'number') {
    return formatFloat(tp, 2)
  }
  return calculationVersion.value.data.calculation_summary.total_price_without_dph
})

const minProformaValue = computed(() => {
  const response = czechNumberToNumber(totalPriceWithVatRounded.value) / 2
  return Math.round(response)
})

const calculatedProformaValue = computed(() => {
  const tpe = czechNumberToNumber(totalPriceWithVatRounded.value)
  console.log('Calculating proforma value for TPE:', tpe)
  console.log('Current proforma value absolute:', proformaValueAbsolute.value)
  if (proformaValueAbsolute.value) {
    const response = proformaValueAbsolute.value / (tpe / 100)
    return response.toFixed(6)
  }
  return null
})

const totalPriceWithVat = computed(() => {
  const response = czechNumberToNumber(totalPriceWithoutVat.value) * (1 + (calculation.value.vat / 100))
  return formatFloat(response, 2)
})

const totalPriceWithVatRounded = computed(() => {
  const response = Math.round(czechNumberToNumber(totalPriceWithVat.value))
  return formatFloat(response, 2)
})

const vatCorrection = computed(() => {
  if (calculation.value.vat === '0' || calculation.value.vat === 0) {
    return formatFloat(0, 2)
  }
  const tpw = czechNumberToNumber(totalPriceWithVat.value)
  const tpwr = czechNumberToNumber(totalPriceWithVatRounded.value)
  return formatFloat(tpwr - tpw, 2)
})

const totalVat = computed(() => {
  const tpe = czechNumberToNumber(totalPriceWithoutVat.value)
  const tpw = czechNumberToNumber(totalPriceWithVat.value)
  return formatFloat(tpw - tpe, 2)
})

const isFormValid = computed(() => {
  const sectionsValid = sectionValidates.value.basic &&
      sectionValidates.value.customer &&
      sectionValidates.value.proforma &&
      sectionValidates.value.detail;

  return sectionsValid &&
      proformaValueAbsolute.value !== null &&
      proformaValidation.value &&
      calculation.value.deliveryTime.trim() !== '' &&
      calculation.value.vat !== -1
})

const versionName = computed(() => {
  let versionNameStr = ""
  if (calculationVersion.value.tag.includes('V')) {
    versionNameStr = calculationVersion.value.tag
  } else {
    versionNameStr = calculation.value.number
  }
  return `${calculationVersion.value.pdf_type ? calculationVersion.value.pdf_type : ''}${versionNameStr}`
})

const urlPdfOffer = computed(() => {
  const url = new URL(calculationVersion.value.pdf_file);
  url.searchParams.append('nocache', Date.now());
  return url.toString()
})

// Methods
const cancelOrder = async () => {
  // Navigace zpět nebo vymazání formuláře
  if (confirm('Opravdu chcete zrušit vytváření objednávky? Všechny zadané údaje budou ztraceny.')) {
    await router.go(-1) // Návrat na předchozí stránku
  }
}

const createOrder = async () => {
  await store.dispatch('calculation_list/setFocusedRow', calculationVersion.value.calculation_uid)
  if (!isFormValid.value) {
    alert('Prosím vyplňte všechna povinná pole.')
    return
  }

  const orderData = {
    calculation_version_id: calculationVersion.value.id,
    update_data: {
      customer: calculation.value.customer,
      delivery_address: calculation.value.deliveryAddress,
      proforma: calculatedProformaValue.value,
      proforma_data: {
        proforma_create: proformaCreate.value,
        due: proformaDue.value,
        payment_method: proformaPaymentMethod.value,
        line_text: proformaLineText.value,
        vat_rate: calculation.value.vat,
        price: proformaValueAbsolute.value,
      },
      delivery_time: calculation.value.deliveryTime,
      vat: calculation.value.vat,
      delegated_vat: calculation.value.delegatedVat,
      internal_note: calculation.value.internalNote,
      order_number: calculationVersion.value.order_number,
      commission_rate: commissionRate.value,
      commission_price: commissionPrice.value,
    },
    order_text: calculationVersion.value.order_text,
  }
  await myApi.orderInsert(orderData)
  await router.go(-1)

}

onMounted(async () => {

  if (props.version && parseInt(props.version) > 0) {
    try {
      calculationVersion.value = await myApi.getCalculationVersion(props.version)
      console.log(calculationVersion.value)

      if (!calculationVersion.value) {
        alert("Nepodařilo se načíst verzi kalkulace.")
        await router.push({name: 'home'})
        return
      }

      if (!calculationVersion.value.pdf_type) {
        alert("Není definována verze nabídky - S, M, L v databázi. Jedná se nekompatibilní data, staré verze Kalkulátoru. Pro vytvoření objednávky je nejprve nutné vygenerovat novou verzi kalkulace, která bude obsahovat platná data, ze kterých je možné vytvořit objednávku!")
        await router.push({name: 'calculationVersions', query: {cid: calculationVersion.value.calculation_uid}})
        return
      }

      const totalPrice = czechNumberToNumber(calculationVersion.value.data.calculation_summary.total_price_with_dph)
      console.log('Total price with DPH:', totalPrice)

      if (calculationVersion.value.calculation_uid) {
        try {
          await store.dispatch('calculation/setCalculationById', calculationVersion.value.calculation_uid)
          calculation.value = store.getters['calculation/getCalculation']

          if (calculation.value) {
            isLoaded.value = true
            proformaValueAbsolute.value = Math.round(totalPrice * (calculation.value.proformaValue / 100))
            console.log('Proforma value absolute:', proformaValueAbsolute.value)
          } else {
            alert("Nepodařilo se načíst data kalkulace.")
            await router.push({name: 'home'})
          }
        } catch (error) {
          console.error("Chyba při načítání kalkulace:", error)
          alert("Nepodařilo se načíst data kalkulace.")
          await router.push({name: 'home'})
        }
      }

    } catch (error) {
      console.error("Chyba při načítání verze kalkulace:", error)
      alert("Nepodařilo se načíst verzi kalkulace.")
      await router.push({name: 'home'})
    }
  } else {
    alert("Neplatné ID verze kalkulace.")
    await router.push({name: 'home'})
  }
})

</script>

<style scoped>
.myTopMargin {
  margin-top: 1rem;
}

.card-header h5 {
  margin-bottom: 0;
}

.form-control:focus,
.form-select:focus {
  border-color: #0d6efd;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}

.btn-outline-secondary:hover {
  color: #fff;
  background-color: #6c757d;
  border-color: #6c757d;
}

textarea {
  resize: vertical;
}
</style>